import type { ProfileTemplateFieldPoliciesDto } from '@up2stay/up2promo-api-client';
import {
  DEFAULT_PROFILE_ACCENT,
  normalizeProfileAccentColor,
} from '@up2stay/up2promo-ui';
import type { UseFormSetValue } from 'react-hook-form';

import type { TemplateOverrideDetail } from '@/core/components/profile-templates/template-customization-hint';
import {
  isFieldLocked,
  type ProfileTemplateFieldKey,
} from '@/core/lib/profile-template-fields';
import { type TemplateFormData } from '@/core/lib/profile-template-form';

export type TemplateFormFieldDisplayLabels = {
  emptyValue: string;
  yes: string;
  no: string;
  language: Record<'auto' | 'de' | 'en', string>;
  visibility: Record<'private' | 'unlisted', string>;
  leadFormEnabled: string;
  leadFormVisibility: string;
  leadFormDelay: string;
  leadFormDelaySeconds: string;
  leadFormVisibilityMode: {
    off?: string;
    manual?: string;
    autoOpen?: string;
  };
  leadsAutoShare: string;
};

export type TemplateFormFieldDisplay = {
  single?: string;
  details?: TemplateOverrideDetail[];
};

function displayText(
  value: string | null | undefined,
  emptyValue: string,
): string {
  const trimmed = normalizeText(value);
  return trimmed || emptyValue;
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function isTemplateTextFieldSet(value: string | null | undefined): boolean {
  return normalizeText(value).length > 0;
}

/** Lead form is considered set when enabled on the template. */
function isTemplateLeadFormSet(template: TemplateFormData): boolean {
  return template.leadFormEnabled === true;
}

const LEAD_FORM_VALUE_KEYS = [
  'leadFormEnabled',
  'leadFormVisibilityMode',
  'leadFormAutoOpenDelay',
] as const satisfies ReadonlyArray<keyof TemplateFormData>;

function normalizeBoolean(value: boolean | undefined): boolean {
  return value === true;
}

function normalizeLeadFormDelay(value: number | null | undefined): number {
  return typeof value === 'number' ? value : 0;
}

function normalizeLeadFormVisibilityMode(
  value: TemplateFormData['leadFormVisibilityMode'] | undefined,
): TemplateFormData['leadFormVisibilityMode'] {
  return value ?? 'manual';
}

function effectiveAccentColor(value: string | null | undefined): string {
  return normalizeProfileAccentColor(value) ?? DEFAULT_PROFILE_ACCENT;
}

function isLeadFormFieldEqual(
  fieldKey: (typeof LEAD_FORM_VALUE_KEYS)[number],
  current: TemplateFormData,
  template: TemplateFormData,
): boolean {
  switch (fieldKey) {
    case 'leadFormEnabled':
      return (
        normalizeBoolean(current.leadFormEnabled) ===
        normalizeBoolean(template.leadFormEnabled)
      );
    case 'leadFormVisibilityMode':
      return (
        normalizeLeadFormVisibilityMode(current.leadFormVisibilityMode) ===
        normalizeLeadFormVisibilityMode(template.leadFormVisibilityMode)
      );
    case 'leadFormAutoOpenDelay':
      return (
        normalizeLeadFormDelay(current.leadFormAutoOpenDelay) ===
        normalizeLeadFormDelay(template.leadFormAutoOpenDelay)
      );
    default:
      return true;
  }
}

/** True when a free (individuell) field differs from the template baseline. */
export function isFormFieldCustomized(
  key: ProfileTemplateFieldKey,
  current: TemplateFormData,
  template: TemplateFormData,
): boolean {
  switch (key) {
    case 'jobTitle':
    case 'company':
    case 'location':
      if (!isTemplateTextFieldSet(template[key])) {
        return false;
      }
      return normalizeText(current[key]) !== normalizeText(template[key]);
    case 'showProfileImage':
      return current.showProfileImage !== template.showProfileImage;
    case 'showUp2promoBranding':
      return current.showUp2promoBranding !== template.showUp2promoBranding;
    case 'language':
      return current.language !== template.language;
    case 'visibility':
      return current.visibility !== template.visibility;
    case 'leadsAutoShareWithWorkspace':
      return (
        current.leadsAutoShareWithWorkspace !==
        template.leadsAutoShareWithWorkspace
      );
    case 'leadForm':
      if (!isTemplateLeadFormSet(template)) {
        return false;
      }
      return LEAD_FORM_VALUE_KEYS.some(
        (fieldKey) => !isLeadFormFieldEqual(fieldKey, current, template),
      );
    case 'accentColor':
      return (
        effectiveAccentColor(current.accentColor) !==
        effectiveAccentColor(template.accentColor)
      );
    case 'layout':
      return (current.layout ?? 'cards') !== (template.layout ?? 'cards');
    case 'theme':
      return (current.theme ?? 'auto') !== (template.theme ?? 'auto');
    case 'headerStyle':
      return (
        (current.headerStyle ?? 'gradient') !==
        (template.headerStyle ?? 'gradient')
      );
    case 'headerImage':
      return (
        (current.headerImage?.id ?? null) !== (template.headerImage?.id ?? null)
      );
    case 'buttonShape':
      return (current.buttonShape ?? null) !== (template.buttonShape ?? null);
    default:
      return false;
  }
}

export function canShowFormFieldOverrideHint(
  key: ProfileTemplateFieldKey,
  policies: ProfileTemplateFieldPoliciesDto,
  templateGoverned: boolean,
  formSyncBlocked: boolean,
): boolean {
  if (!templateGoverned || formSyncBlocked) return false;
  return !isFieldLocked(policies, key);
}

export function applyTemplateFormFieldReset<
  T extends TemplateFormData & Record<string, unknown>,
>(
  key: ProfileTemplateFieldKey,
  template: TemplateFormData,
  setValue: UseFormSetValue<T>,
): void {
  switch (key) {
    case 'jobTitle':
      setValue('jobTitle' as never, template.jobTitle as never);
      break;
    case 'company':
      setValue('company' as never, template.company as never);
      break;
    case 'location':
      setValue('location' as never, template.location as never);
      break;
    case 'showProfileImage':
      setValue('showProfileImage' as never, template.showProfileImage as never);
      break;
    case 'showUp2promoBranding':
      setValue(
        'showUp2promoBranding' as never,
        template.showUp2promoBranding as never,
      );
      break;
    case 'language':
      setValue('language' as never, template.language as never);
      break;
    case 'visibility':
      setValue('visibility' as never, template.visibility as never);
      break;
    case 'leadsAutoShareWithWorkspace':
      setValue(
        'leadsAutoShareWithWorkspace' as never,
        template.leadsAutoShareWithWorkspace as never,
      );
      break;
    case 'leadForm':
      for (const fieldKey of LEAD_FORM_VALUE_KEYS) {
        setValue(fieldKey as never, template[fieldKey] as never);
      }
      break;
    case 'accentColor':
      setValue('accentColor' as never, template.accentColor as never);
      break;
    case 'layout':
      setValue('layout' as never, template.layout as never);
      break;
    case 'theme':
      setValue('theme' as never, template.theme as never);
      break;
    case 'headerStyle':
      setValue('headerStyle' as never, template.headerStyle as never);
      break;
    case 'headerImage':
      setValue('headerImage' as never, template.headerImage as never);
      break;
    case 'buttonShape':
      setValue('buttonShape' as never, template.buttonShape as never);
      break;
    default:
      break;
  }
}

function formatLeadFormVisibility(
  mode: TemplateFormData['leadFormVisibilityMode'],
  labels: TemplateFormFieldDisplayLabels,
): string {
  if (mode === 'off') {
    return labels.leadFormVisibilityMode.off ?? 'off';
  }
  if (mode === 'auto_open') {
    return labels.leadFormVisibilityMode.autoOpen ?? 'auto_open';
  }
  return labels.leadFormVisibilityMode.manual ?? 'manual';
}

/** Human-readable template value(s) for override hints. */
export function getTemplateFormFieldDisplay(
  key: ProfileTemplateFieldKey,
  current: TemplateFormData,
  template: TemplateFormData,
  labels: TemplateFormFieldDisplayLabels,
): TemplateFormFieldDisplay | null {
  if (!isFormFieldCustomized(key, current, template)) {
    return null;
  }

  switch (key) {
    case 'jobTitle':
    case 'company':
    case 'location':
      return { single: displayText(template[key], labels.emptyValue) };
    case 'showProfileImage':
      return {
        single: template.showProfileImage ? labels.yes : labels.no,
      };
    case 'showUp2promoBranding':
      return {
        single: template.showUp2promoBranding ? labels.yes : labels.no,
      };
    case 'language':
      return { single: labels.language[template.language] };
    case 'visibility':
      return { single: labels.visibility[template.visibility] };
    case 'leadsAutoShareWithWorkspace':
      return {
        single: template.leadsAutoShareWithWorkspace ? labels.yes : labels.no,
      };
    case 'leadForm': {
      const details: TemplateOverrideDetail[] = [];
      if (!isLeadFormFieldEqual('leadFormEnabled', current, template)) {
        details.push({
          label: labels.leadFormEnabled,
          value: template.leadFormEnabled ? labels.yes : labels.no,
        });
      }
      if (!isLeadFormFieldEqual('leadFormVisibilityMode', current, template)) {
        details.push({
          label: labels.leadFormVisibility,
          value: formatLeadFormVisibility(
            template.leadFormVisibilityMode,
            labels,
          ),
        });
      }
      if (!isLeadFormFieldEqual('leadFormAutoOpenDelay', current, template)) {
        details.push({
          label: labels.leadFormDelay,
          value: labels.leadFormDelaySeconds.replace(
            '{seconds}',
            String(normalizeLeadFormDelay(template.leadFormAutoOpenDelay)),
          ),
        });
      }
      return details.length > 0 ? { details } : null;
    }
    case 'accentColor':
      return { single: effectiveAccentColor(template.accentColor) };
    case 'layout':
      return { single: template.layout ?? 'cards' };
    case 'theme':
      return { single: template.theme ?? 'auto' };
    case 'headerStyle':
      return { single: template.headerStyle ?? 'gradient' };
    case 'headerImage':
      return {
        single: template.headerImage?.id
          ? template.headerImage.path || template.headerImage.id
          : labels.emptyValue,
      };
    case 'buttonShape':
      return { single: template.buttonShape ?? labels.emptyValue };
    default:
      return null;
  }
}
